import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LIST_PAGES, locationPinPath, verifyLocationPath, editProblems, hasChangedFields, mapsLinkPin, quickCreateTitle, fieldDefault, t, visibleEditFields, type EditField, type MapsLinkAnswer, type MapsLinkPin, type PagedRecord, type QuickCreateOpener } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { FormFrame } from '../../components/forms';
import modalStyles from '../../components/modals/modal.module.css';
import { PAGES_PATH, post } from './api';
import { DuplicateModal, type DuplicateHit } from './DuplicateModal';
import { LocationVerifyStep, addressFromFields } from './LocationVerify';
import { MapsLinkPaste, readMapsLinkAnswer } from './MapsLinkPaste';
import { RecordFields } from './RecordFields';
import { EMPTY_SERVICE_REQUEST, ServiceRequestFields, type ServiceRequestDraft } from './ServiceRequestFields';
import { PartsDealFields, emptyPartsDeal, type PartsDealDraftState } from './PartsDealFields';
import { SHORT_FORM_FIELDS, saveDealForm } from './dealForms';
import { useItemPicker } from '../settings/api';
import { useViewerZone } from '../../shell/ViewerZone';
import type { LocationVerifyAnswer, VerifyChoice } from '@bms2/shared';

/**
 * A list page's + New X, and the Quick-Create Modal opened from a Dropdown's
 * Add New row (piece 13). 🔨 It nests the record page's own field form, which
 * is why there is one form here and not a second, thinner one that drifts from
 * the page's.
 *
 * 💰 The Company form's stated minimum set names EIN and W-9-on-file status.
 * Neither is writable yet: `ein` ships as an `EncryptedField` whose key seam
 * Auth, real logins, and turning enforcement on builds, and W-9 status is a
 * `VendorTaxProfile` column that Money out, banking, payments, expenses
 * declares. The form carries the fields that exist.
 *
 * A Site is not created here: its own combo modal writes the building, the
 * site, its roles and the first note in one transaction.
 */
export interface NewRecordModalProps {
  open: boolean;
  /**
   * 🔨 A Site is not created here, and the list register is what refuses it: its
   * own entry says + New opens the combo window that writes the building, the
   * site, its roles and the first note together (T180). The type excluded
   * `SiteRecord` until 2026-09-22, which said the same thing twice and left the
   * one list page unable to hand its own record type through.
   */
  recordType: PagedRecord;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** 🔒 `route` names the page to open where it is not the record's own: the Parts form opens its Quote's (decision 2290). */
  onCreated: (created: { id: string; label: string; route?: string }) => void;
  openerId?: string | null;
  /**
   * Fields the opener already knows, filled in before the person types
   * (decision 0497): the Company page raising a Deal knows the account.
   * 🔨 It is read when the modal MOUNTS, so a caller that keeps the modal
   * mounted behind an open flag keys it to what it is creating from.
   */
  initialValues?: Record<string, unknown>;
  /** Which button opened the window, whose words title it (decision 1451); a Dropdown's Add New row where none is named. */
  openedFrom?: QuickCreateOpener;
  /**
   * Fields the create writes beside the record's own, drawn after them and sent and checked with them: a message's
   * sender's company, work address and title, which the Person create writes as an employment (decision 1506).
   */
  extraFields?: readonly EditField[];
  /** A sentence above the form, where the opener has something to say about what it filled: no company was found. */
  lead?: string | null;
  /**
   * Keys the create sends beside the drawn fields and never draws: the texted card's offer its save takes, so the
   * create route takes the offer in the same transaction as the record (decision 5180).
   */
  sendAlso?: Readonly<Record<string, string>>;
}

/** One empty list, so a caller passing none does not re-arm the save on every render. */
const NO_EXTRA_FIELDS: readonly EditField[] = [];

interface CreatedAnswer {
  id: string;
  label: string;
  duplicates: DuplicateHit[];
  /** The page the save opens where it is not the record's own (decision 2290). */
  route?: string;
  /** Why an emergency's confirmation text did not go after the Deal and job were saved (T1781). */
  textNotSent?: string;
}

/**
 * The duplicate question, with the routing that Open the existing one needs.
 * 🔨 It is its own component because the router hook belongs where the modal
 * does — mounted only on a match — and never on the create screen's own path,
 * which is drawn in places that carry no router.
 */
function DuplicatePrompt({ kind, subject, hits, onKeepBoth, onLeave }: { kind: 'person' | 'record'; subject: string; hits: readonly DuplicateHit[]; onKeepBoth: () => void; onLeave: () => void }) {
  const navigate = useNavigate();
  return (
    <DuplicateModal
      open
      kind={kind}
      subject={subject}
      hits={hits}
      onKeepBoth={onKeepBoth}
      onUseExisting={(hit) => {
        onLeave();
        navigate(hit.href);
      }}
      onCancel={onKeepBoth}
    />
  );
}

/** Whether the Service form holds anything a person put there: any answer that differs from the empty draft. */
function serviceDraftTyped(draft: ServiceRequestDraft): boolean {
  return (Object.keys(EMPTY_SERVICE_REQUEST) as (keyof ServiceRequestDraft)[]).some((key) => draft[key] !== EMPTY_SERVICE_REQUEST[key]);
}

/** Whether the Parts grid holds anything a person put there; a line counts once it names a part or carries words, as the save reads it. */
function partsDraftTyped(draft: PartsDealDraftState): boolean {
  const empty = emptyPartsDeal();
  return draft.askedById !== empty.askedById || draft.ownerId !== empty.ownerId || draft.expectedDate !== empty.expectedDate || draft.shipToSiteId !== empty.shipToSiteId
    || draft.customerPoNumber !== empty.customerPoNumber || draft.need.trim() !== '' || draft.lines.some((line) => line.kind !== 'divider' && (line.itemId || line.itemLabel.trim() || line.description.trim()));
}

export function NewRecordModal({ open, recordType, apiBaseUrl = '', onCancel, onCreated, openerId = null, initialValues, openedFrom = 'addNew', extraFields = NO_EXTRA_FIELDS, lead = null, sendAlso }: NewRecordModalProps) {
  // 🔨 Held as state of its own, so a caller passing a fresh object each render does not re-arm the
  // resets below. It is what the modal MOUNTED with, which is the contract the prop's comment states.
  const [opened] = useState<Record<string, unknown>>(() => initialValues ?? {});
  const [values, setValues] = useState<Record<string, unknown>>(opened);
  const [error, setError] = useState<string | null>(null);
  /**
   * 🔨 The row saved and the match is named: the modal reports why it matched
   * and offers Open the existing one, Keep both and Cancel. 💰 A banner set on
   * the create screen was never read, because the screen closed in the same
   * breath as the banner was written (found while building, 2026-09-15).
   */
  const [matched, setMatched] = useState<{ subject: string; created: { id: string; label: string }; hits: DuplicateHit[] } | null>(null);
  /** The Location form's Verify step: what its answer lands on the record once it exists (decision 0338). */
  const [landing, setLanding] = useState<{ outcome: LocationVerifyAnswer['outcome']; choice: VerifyChoice | null } | null>(null);
  /** The point a pasted Maps link gave, written as the pin once the record exists (decision 1600). */
  const [linkPin, setLinkPin] = useState<MapsLinkPin | null>(null);
  // A closed modal holds no draft and no banner; both are derived from `open`.
  const shownError = open ? error : null;
  /** 🔒 The Service type draws the Service form, the request route's own, beneath the Account and the Type (decision 2290). */
  const [request, setRequest] = useState<ServiceRequestDraft>(EMPTY_SERVICE_REQUEST);
  const isRequest = recordType === 'Deal' && values.dealType === 'Service';
  /** 🔒 The Type picks the form beneath it: Parts draws the Parts form, with no route, value or stage to ask (decision 2290). */
  const [parts, setParts] = useState<PartsDealDraftState>(emptyPartsDeal);
  const isParts = recordType === 'Deal' && values.dealType === 'Parts';
  const picker = useItemPicker(apiBaseUrl, 'sales');
  const zone = useViewerZone();

  /** Keep both: the created row stands, and the caller is handed it as any create hands it back. */
  const keepBoth = useCallback(() => {
    if (!matched) return;
    onCreated(matched.created);
    setMatched(null);
    setValues(opened);
    setLanding(null);
    setLinkPin(null);
    onCancel();
  }, [matched, onCancel, onCreated, opened]);

  /** Open the existing one: the created row still exists, and the person lands on the record they already had. */
  const leaveForExisting = useCallback(() => {
    setMatched(null);
    setValues(opened);
    setLanding(null);
    setLinkPin(null);
    onCancel();
  }, [onCancel, opened]);

  /**
   * 🔨 A Location saved whose link pin did not write stays open with the refusal
   * (decision 1600): the record exists, so Save again retries the pin alone
   * rather than creating the Location twice.
   */
  const [pinPending, setPinPending] = useState<CreatedAnswer | null>(null);
  /** A Deal saved whose emergency text did not go stays open once with the notice; Save again carries on, and books nothing twice (T1781). */
  const [textPending, setTextPending] = useState<CreatedAnswer | null>(null);
  /**
   * 🔒 Cancel drops the whole draft, so the next + New opens clean (decision 3063). 💰 The list pages keep this window
   * mounted behind an open flag, and Cancel forgot only a held Deal, so the next opening showed what was abandoned.
   * The Form frame asks Leave Without Saving? first where anything was typed (decision 3061).
   */
  const cancel = useCallback(() => {
    setValues(opened);
    setRequest(EMPTY_SERVICE_REQUEST);
    setParts(emptyPartsDeal());
    setLanding(null);
    setLinkPin(null);
    setError(null);
    setPinPending(null);
    setTextPending(null);
    onCancel();
  }, [onCancel, opened]);

  /** The pin a pasted link gave, written on the saved Location; false where the write was refused and the modal stays open. */
  const writeLinkPin = useCallback(
    async (saved: CreatedAnswer): Promise<boolean> => {
      if (recordType !== 'LocationRecord' || !linkPin) return true;
      const pinned = await post(locationPinPath(saved.id), linkPin, apiBaseUrl);
      if (pinned.ok) {
        setPinPending(null);
        return true;
      }
      setPinPending(saved);
      setError(`${t('mapsLink.pinFailed')} ${pinned.message}`);
      return false;
    },
    [apiBaseUrl, linkPin, recordType],
  );

  /** The saved row handed back, or the duplicate question asked first. */
  const finish = useCallback(
    (saved: CreatedAnswer) => {
      const created = { id: saved.id, label: saved.label, ...(saved.route ? { route: saved.route } : {}) };
      // 🔨 The row saves either way; a match is named and never resolved for them.
      // The create screen stays behind the duplicate modal until the person answers.
      if ((saved.duplicates ?? []).length > 0) {
        setMatched({ subject: created.label, created, hits: saved.duplicates });
        return;
      }
      onCreated(created);
      setValues(opened);
      setRequest(EMPTY_SERVICE_REQUEST);
      setParts(emptyPartsDeal());
      setLanding(null);
      setLinkPin(null);
      onCancel();
    },
    [onCancel, onCreated, opened],
  );

  const save = useCallback(async () => {
    setError(null);
    if (pinPending) {
      if (await writeLinkPin(pinPending)) finish(pinPending);
      return;
    }
    if (textPending) {
      setTextPending(null);
      finish(textPending);
      return;
    }
    /*
     * 🔒 The Parts and Service forms save through the one by-type save the New Deal page calls (decisions 2290, 2510):
     * a picker's + Add on a Deal still opens this window, because the person returns to the page beneath.
     */
    if (isParts || isRequest) {
      const made = await saveDealForm({ values, parts, request, listPriceOf: (itemId) => picker.prices.current.get(itemId) ?? null, zone, apiBaseUrl });
      if (!made.ok) {
        setError(made.message);
        return;
      }
      if (made.data.textNotSent) {
        // 🔒 The Deal is saved, so the notice is a warning and not an error, and edits made now are not applied (T1819).
        setTextPending({ ...made.data, duplicates: [] });
        return;
      }
      finish({ ...made.data, duplicates: [] });
      return;
    }
    // The fields the form drew, a choice left untouched sent as its default and a hidden field left off (decision 0353).
    const drawn = visibleEditFields(recordType, 'create', (key) => values[key]).concat(extraFields);
    const body = Object.fromEntries(drawn.map((field) => [field.key, values[field.key] ?? fieldDefault(field, (key) => values[key]) ?? null]));
    const missing = editProblems(recordType, body).concat(extraFields.filter((field) => field.required && String(body[field.key] ?? '').trim() === '').map((field) => field.label));
    if (missing.length > 0) {
      setError(`${t('edit.required')} ${missing.join(', ')}`);
      return;
    }
    const answer = await post<CreatedAnswer>(`${PAGES_PATH}/records/${recordType}`, sendAlso ? { ...body, ...sendAlso } : body, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    /**
     * A building verified in the form lands its answer now that the record
     * exists (decision 0338). 🔨 It always carries a choice, so the server reads
     * the row the form's own press wrote rather than pressing again: a press
     * without one asks the provider afresh (decision 0672).
     */
    if (recordType === 'LocationRecord' && landing && (landing.outcome !== 'Corrected' || landing.choice)) {
      await post(verifyLocationPath(answer.data.id), { choice: landing.choice ?? 'accept' }, apiBaseUrl);
    }
    // 🔒 A pasted link's point lands as the pin, written as the person's own with the link beside it (decision 1600).
    if (!(await writeLinkPin(answer.data))) return;
    finish(answer.data);
  }, [apiBaseUrl, extraFields, finish, isParts, isRequest, landing, parts, picker.prices, pinPending, recordType, request, sendAlso, textPending, values, writeLinkPin, zone]);

  /** The fields a link's answer fills, each for the person to read and correct; a part the answer lacks is left as typed. */
  const fillFromLink = (place: MapsLinkAnswer) => {
    setLinkPin(mapsLinkPin(place));
    const address = place.address;
    if (!address) return;
    setValues((held) => ({
      ...held,
      countryCode: address.countryCode,
      ...(address.addressLines.length > 0 ? { addressLines: address.addressLines } : {}),
      ...(address.locality ? { locality: address.locality } : {}),
      ...(address.municipality ? { municipality: address.municipality } : {}),
      ...(address.administrativeArea ? { administrativeArea: address.administrativeArea } : {}),
      ...(address.county ? { county: address.county } : {}),
      ...(address.postalCode ? { postalCode: address.postalCode } : {}),
    }));
  };

  const shownMatch = open ? matched : null;
  /**
   * 🔒 What the leave guard reads (decision 3061): a field moved from what the window opened on, or a pasted link's pin.
   * 🔨 Nothing is typed once the duplicate question stands, because the row is already saved: Keep both and Open the
   * existing one navigate in the press itself, and a hold still drawn then swallowed the move (found while building, 2026-10-11).
   */
  // 🔨 The Service request and the Parts grid keep their drafts outside `values` (T2248), so they are read beside it; an emergency text
  // waiting on the notice means the Deal is already saved, and nothing is typed then, as with the duplicate question (T2248).
  const typed = open && matched === null && textPending === null
    && (hasChangedFields(opened, values) || linkPin !== null || (isRequest && serviceDraftTyped(request)) || (isParts && partsDraftTyped(parts)));

  return (
    <>
    {shownMatch ? (
      <DuplicatePrompt
        kind={recordType === 'PersonRecord' ? 'person' : 'record'}
        subject={shownMatch.subject}
        hits={shownMatch.hits}
        onKeepBoth={keepBoth}
        onLeave={leaveForExisting}
      />
    ) : null}
    <FormFrame
      host="window"
      open={open}
      title={quickCreateTitle(LIST_PAGES[recordType].singular, openedFrom)}
      onCancel={cancel}
      openerId={openerId}
      // The parts grid wants the document forms' width.
      width={isParts ? 'wide' : 'regular'}
      dataForm="new-record"
      typed={typed}
      refusal={shownError}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('modal.save'), onAct: () => save() }}
    >
      {/* 🔒 One fixed gap between a field and the next label, the Body's own (decision 1451). */}
      <div className={modalStyles.form} data-new-record={recordType}>
        {open && textPending?.textNotSent ? <AlertBanner tone="warning" message={`${textPending.textNotSent} ${t('deal.new.textThenCarryOn')}`} action={{ label: t('deal.new.openSaved'), onAction: () => void save() }} /> : null}
        {lead ? <p className={modalStyles.lead} data-new-record-lead>{lead}</p> : null}
        {open && recordType === 'LocationRecord' ? <MapsLinkPaste read={(link) => readMapsLinkAnswer(link, apiBaseUrl)} onPlace={(place) => fillFromLink(place)} onCleared={() => setLinkPin(null)} /> : null}
        {/* 🔒 The Parts and Service forms keep the Account and the Type and ask their own facts beneath them (decision 2290). */}
        <RecordFields recordType={recordType} read={(key) => values[key]} onChange={(key, value) => setValues((held) => ({ ...held, [key]: value }))} apiBaseUrl={apiBaseUrl} mode="create" extra={extraFields} only={isParts || isRequest ? SHORT_FORM_FIELDS : null} />
        {open && isParts ? <PartsDealFields companyId={typeof values.companyId === 'string' && values.companyId ? values.companyId : null} draft={parts} onChange={setParts} apiBaseUrl={apiBaseUrl} picker={picker} /> : null}
        {open && isRequest ? <ServiceRequestFields companyId={typeof values.companyId === 'string' && values.companyId ? values.companyId : null} draft={request} onChange={setRequest} apiBaseUrl={apiBaseUrl} /> : null}
        {open && recordType === 'LocationRecord' ? (
          <LocationVerifyStep
            address={addressFromFields((key) => values[key])}
            apiBaseUrl={apiBaseUrl}
            onAccept={(normalized) => setValues((held) => ({ ...held, addressLines: normalized.addressLines, locality: normalized.locality ?? '', ...(normalized.municipality ? { municipality: normalized.municipality } : {}), administrativeArea: normalized.administrativeArea ?? '', ...(normalized.county ? { county: normalized.county } : {}), postalCode: normalized.postalCode ?? '' }))}
            onAnswered={setLanding}
          />
        ) : null}
      </div>
    </FormFrame>
    </>
  );
}
